import React, { useState, useCallback } from 'react';
import { useVisualizationStore } from './store/visualizationStore';
import { VisualizationEngine } from './components/VisualizationEngine';
import { WebGLRenderer } from './components/WebGLRenderer';
import { ZoomTransition } from './components/ZoomTransition';
import { NavigationPanel } from './components/NavigationPanel';
import { SearchPanel } from './components/SearchPanel';
import { InfoPanel } from './components/InfoPanel';
import { ControlsPanel } from './components/ControlsPanel';
import { SampleTopologySelector } from './components/SampleTopologySelector';
import { TopologyEditorPanel } from './components/TopologyEditorPanel';
import { CookieConsent } from './components/CookieConsent';
import { InfragraphInfrastructure } from './types/infrastructure';
import { parseInfrastructureContent } from './utils/parseInfrastructureContent';
import { validateInfragraphFormat } from './utils/validateInfragraphFormat';

function App() {
  const [useWebGL, setUseWebGL] = useState(false);
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const [loadError, setLoadError] = useState<string | null>(null);

  const { infrastructure, loadInfrastructure, isLoading, error, reset, toggleEditor } = useVisualizationStore();

  // Handle file upload
  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Clear any previous errors
    setLoadError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const fileContent = e.target?.result as string;

        // Parse and validate infragraph format
        const data = parseInfrastructureContent(fileContent, file.name);
        validateInfragraphFormat(data, file.name);

        loadInfrastructure(data as InfragraphInfrastructure);
        setLoadError(null); // Clear error on successful load
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error('Failed to parse infrastructure file:', error);
        setLoadError(`Failed to load "${file.name}": ${errorMessage}`);
      }
    };
    reader.readAsText(file);
  }, [loadInfrastructure]);


  // Sample topology loading handlers
  const handleSampleLoadStart = useCallback(() => {
    setLoadError(null);
  }, []);

  const handleSampleLoadComplete = useCallback(() => {
    setLoadError(null);
  }, []);

  const handleSampleLoadError = useCallback((errorMessage: string) => {
    setLoadError(errorMessage);
  }, []);


  // Update dimensions on resize
  React.useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;

      setDimensions({
        width: newWidth,
        height: newHeight
      });

      // Only recalculate viewport for manual layouts
      // Manual layout uses 2000x2000 reference space with (0,0) at center (1000, 1000)
      const { nodes, setViewport } = useVisualizationStore.getState();
      const hasManualLayout = nodes.some(node => node.fx !== undefined || node.fy !== undefined);

      if (hasManualLayout) {
        const MANUAL_LAYOUT_CENTER = 1000;
        setViewport({
          x: newWidth / 2 - MANUAL_LAYOUT_CENTER,
          y: newHeight / 2 - MANUAL_LAYOUT_CENTER
        });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-red-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Reload Application
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app min-h-screen bg-gray-100">
      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg text-center">
            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-700">Loading infrastructure data...</p>
          </div>
        </div>
      )}

      {/* Welcome Screen */}
      {!infrastructure && !isLoading && (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center max-w-6xl w-full">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Netreplica Graphite
            </h1>
            <p className="text-gray-600 mb-8">
              Visualize infrastructure from system components to global networks.
            </p>

            {/* Error Display */}
            {loadError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{loadError}</p>
              </div>
            )}

            {/* File Upload Section */}
            <div className="mb-8">
              <div>
                <input
                  type="file"
                  accept=".json,.yaml,.yml"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors font-medium"
                >
                  <span className="mr-2">📁</span>
                  Load Infrastructure File
                </label>
              </div>
              <p className="text-gray-500 text-sm mt-2">
                Supports JSON and YAML files in <a href="https://infragraph.dev/" target="_blank" rel="noreferrer">InfraGraph</a> format
              </p>
            </div>

            <div className="text-gray-400 font-medium mb-6">— OR —</div>

            {/* Sample Topology Categories */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Load Sample Topologies</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
                {/* Hierarchical Topologies */}
                <div className="bg-white rounded-lg shadow-md border p-6 hover:shadow-lg transition-shadow">
                  <div className="text-center mb-4">
                    <div className="text-3xl mb-2">🏗️</div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Hierarchical</h4>
                    <p className="text-gray-600 text-sm">Leaf-Spine multi-tier topologies</p>
                  </div>
                  <SampleTopologySelector
                    category="hierarchical"
                    onLoadStart={handleSampleLoadStart}
                    onLoadComplete={handleSampleLoadComplete}
                    onLoadError={handleSampleLoadError}
                  />
                </div>

                {/* Ring Topologies */}
                <div className="bg-white rounded-lg shadow-md border p-6 hover:shadow-lg transition-shadow">
                  <div className="text-center mb-4">
                    <div className="text-3xl mb-2">🔁</div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Ring</h4>
                    <p className="text-gray-600 text-sm">Single and dual rings</p>
                  </div>
                  <SampleTopologySelector
                    category="ring"
                    onLoadStart={handleSampleLoadStart}
                    onLoadComplete={handleSampleLoadComplete}
                    onLoadError={handleSampleLoadError}
                  />
                </div>

                {/* Dragonfly Topologies */}
                <div className="bg-white rounded-lg shadow-md border p-6 hover:shadow-lg transition-shadow">
                  <div className="text-center mb-4">
                    <div className="text-3xl mb-2">🐉</div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Dragonfly</h4>
                    <p className="text-gray-600 text-sm">Multi-group all-to-all topologies</p>
                  </div>
                  <SampleTopologySelector
                    category="dragonfly"
                    onLoadStart={handleSampleLoadStart}
                    onLoadComplete={handleSampleLoadComplete}
                    onLoadError={handleSampleLoadError}
                  />
                </div>

                {/* Torus Topologies */}
                <div className="bg-white rounded-lg shadow-md border p-6 hover:shadow-lg transition-shadow">
                  <div className="text-center mb-4">
                    <div className="text-3xl mb-2">🧊</div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Torus</h4>
                    <p className="text-gray-600 text-sm">3D mesh with wraparound links</p>
                  </div>
                  <SampleTopologySelector
                    category="torus"
                    onLoadStart={handleSampleLoadStart}
                    onLoadComplete={handleSampleLoadComplete}
                    onLoadError={handleSampleLoadError}
                  />
                </div>
              </div>
            </div>


            <div className="mt-8 text-sm text-gray-500 space-y-1">
              <p><strong>Features:</strong> Leaf-Spine • Ring • Dragonfly • 3D Torus topology autolayout</p>
              <p><strong>Privacy:</strong> Your topology never leaves your browser</p>
              <p><strong>Scale:</strong> Supports 1K+ node visualizations</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Visualization */}
      {infrastructure && !isLoading && (
        <div className="relative w-full h-screen overflow-hidden">
          {/* Visualization Canvas */}
          <div className="w-full h-full relative">
            {useWebGL ? (
              <WebGLRenderer
                width={dimensions.width}
                height={dimensions.height}
                enableWebGL={true}
              />
            ) : (
              <VisualizationEngine
                width={dimensions.width}
                height={dimensions.height}
              />
            )}

            {/* Zoom Transition Manager */}
            <ZoomTransition />

            {/* UI Panels */}
            <NavigationPanel />
            <SearchPanel />
            <InfoPanel />
            <ControlsPanel />

            {/* Topology Editor Panel */}
            <TopologyEditorPanel />

            {/* Top Right Controls - Content Tools */}
            <div className="absolute top-20 right-4 z-10 flex flex-col space-y-2">
              {/* Editor Toggle Button */}
              <button
                onClick={() => toggleEditor()}
                className="bg-white shadow-lg rounded-lg p-3 hover:shadow-xl transition-shadow"
                title="Toggle topology editor"
              >
                ✏️
              </button>
            </div>

            {/* Bottom Left Controls */}
            <div className="absolute bottom-4 left-4 z-10 flex flex-col space-y-2">
              {/* New Topology Button */}
              <button
                onClick={() => reset()}
                className="px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors font-medium"
                title="Load new topology"
              >
                ← New Topology
              </button>

              {/* Rendering Mode Toggle */}
              <button
                onClick={() => setUseWebGL(!useWebGL)}
                className={`px-3 py-2 rounded text-sm transition-colors ${
                  useWebGL
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-600 text-white hover:bg-gray-700'
                }`}
              >
                {useWebGL ? '3D WebGL' : '2D SVG'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Consent Banner */}
      <CookieConsent />
    </div>
  );
}

export default App;
